import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme as MuiTheme } from "@mui/material/styles/createTheme";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { RouterLink } from "src/components/router-link";
import type { FC } from "react";

type HomeTeamProps = {
  extended?: boolean;
};

// Helper function to chunk array
function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

export const HomeTeam: FC<HomeTeamProps> = ({ extended = false }) => {
  const theme = useTheme();
  const smDown = useMediaQuery((theme: MuiTheme) =>
    theme.breakpoints.down("sm")
  );

  const teamMembersExtended = [
    {
      title: "MD, PhD",
      name: "Kelvin Kramp",
      avatar: "/assets/avatars/KK.jpeg",
      blogTitle: "Introducing Kelvin Kramp",
      linkedin: "https://www.linkedin.com/in/kelvin-kramp-a64b4a1b0/",
      basic: true,
    },
    {
      title: "AIOps Specialist",
      name: "Hylke Nicola",
      avatar: "/assets/avatars/HN.png",
      blogTitle: "Introducing Hylke Nicola",
      linkedin: "https://www.linkedin.com/in/hydoskills/",
      basic: true,
    },
    {
      title: "Data Architect",
      name: "Corné Gabriels",
      avatar: "/assets/avatars/CG.png",
      blogTitle: "Introducing Corné Gabriels",
      linkedin: "https://www.linkedin.com/in/corné-gabriëls-b9288916/",
      basic: true,
    },
    {
      title: "Diabetes Educator",
      name: "Lukáš Trčka",
      avatar: "/assets/avatars/LukasTrcka.png",
      blogTitle: "Introducing Lukáš Trčka",
      linkedin: "https://cz.linkedin.com/in/lukastrcka",
      basic: true,
    },
    {
      title: "Cloud Architect",
      name: "Redmer Nauta",
      avatar: "/assets/avatars/RN.png",
      blogTitle: "Introducing Redmer Nauta",
      linkedin: "https://www.linkedin.com/in/redmer-nauta-099528167/",
      basic: false,
    },
    {
      title: "Diabetes Coach",
      name: "Marvin Micka",
      avatar: "/assets/avatars/marvinmicka.png",
      linkedin: "https://www.linkedin.com/in/marvin-micka-80b3ab71/",
      basic: false,
    },
    {
      title: "Consultant",
      name: "Paul Banks",
      avatar: "/assets/avatars/PaulBanks.png",
      blogTitle: "Introducing Paul Banks",
      linkedin: "https://www.linkedin.com/in/paul-banks-8960ab81",
      basic: false,
    },
  ];

  const teamMembers = extended
    ? teamMembersExtended
    : teamMembersExtended.filter((member) => member.basic);

  // Determines how many team members per row depending on screen size
  const maxPerRow = smDown ? 2 : 4;
  const rows = chunkArray(teamMembers, maxPerRow);

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        py: "120px",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* Title */}
          <Typography
            component="h2"
            variant="h2"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: "bold",
              mb: 3,
              fontSize: smDown ? "2rem" : "2.5rem",
              lineHeight: 1.2,
            }}
          >
            Meet the Opensource.clinic team
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              fontSize: "1.1rem",
              lineHeight: 1.6,
              mb: 6,
              maxWidth: "600px",
              opacity: 0.8,
            }}
          >
            A group of engineers and healthcare professionals who live with
            diabetes and believe in user-first innovation.
          </Typography>

          {/* Team Photos */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: smDown ? 3 : 4,
              mb: 6,
              width: "100%",
            }}
          >
            {rows.map((row, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: smDown ? 2 : 4,
                  flexWrap: smDown ? "wrap" : "nowrap",
                  width: "100%",
                  mb: rowIndex !== rows.length - 1 ? (smDown ? 2 : 3) : 0,
                }}
              >
                {row.map((member, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {member.blogTitle ? (
                      <Box
                        component={RouterLink}
                        href={`/blog/details/${encodeURIComponent(
                          member.blogTitle.replace(/\s+/g, "-")
                        )}`}
                      >
                        <Avatar
                          alt={member.name}
                          src={member.avatar}
                          sx={{
                            width: smDown ? 60 : 80,
                            height: smDown ? 60 : 80,
                            border: "3px solid #F3F4F6",
                            cursor: "pointer",
                            transition: "transform 0.2s ease-in-out",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Avatar
                        alt={member.name}
                        src={member.avatar}
                        sx={{
                          width: smDown ? 60 : 80,
                          height: smDown ? 60 : 80,
                          border: "3px solid #F3F4F6",
                          cursor: "default",
                          transition: "transform 0.2s ease-in-out",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />
                    )}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: smDown ? "0.92rem" : "1rem",
                        fontWeight: 400,
                        mt: 0.5,
                        textAlign: "center",
                      }}
                    >
                      {/* You can fill in real titles if you have them, otherwise use "Team Member" as a fallback */}
                      {member.name}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: smDown ? "0.92rem" : "1rem",
                        fontWeight: 200,
                        textAlign: "center",
                        mt: -0.5,
                      }}
                    >
                      {/* You can fill in real titles if you have them, otherwise use "Team Member" as a fallback */}
                      {member?.title || "Team Member"}
                    </Typography>
                    {member.linkedin && (
                      <IconButton
                        component="a"
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{
                          color: "#0077B5",
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 119, 181, 0.08)",
                            transform: "scale(1.1)",
                          },
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        <LinkedInIcon
                          sx={{ fontSize: smDown ? "1.2rem" : "1.4rem" }}
                        />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          {/* Read More Button */}
          {/* <Button
            href={`${process.env.REACT_APP_BASE_URL_LANDING}/blog`}
            variant="contained"
            size="large"
            endIcon={
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  ml: 1,
                }}
              >
                <ArrowForwardIcon
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.primary.main,
                  }}
                />
              </Box>
            }
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: "600",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Read more
          </Button> */}
        </Box>
      </Container>
    </Box>
  );
};
